import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import styles from './TrackDatManual.module.css';

export default function TrackDatManual({
  html,
  toc,
  productRoute = '/TrackDat',
  productName,
  manualTitle,
  manualDek,
  manualDescription,
  alternateManualHref,
  alternateManualLabel,
  documentRootId,
}) {
  const [progress, setProgress] = useState(0);
  const [contentsOpen, setContentsOpen] = useState(false);
  const isTesting = productRoute === '/TrackDatTesting';
  const resolvedProductName = productName || (isTesting ? 'TrackDatTesting' : 'TrackDat');
  const pageTitle = manualTitle || `${resolvedProductName} Manual`;
  const pageDek = manualDek || 'Motion tracking, camera-path design, effects, audio, and multitrack composition for dancers.';
  const pageDescription = manualDescription || 'The complete illustrated TrackDat manual for motion tracking, audio, effects, rendering, and multitrack composition.';
  const secondaryHref = alternateManualHref || (isTesting ? '/TrackDat/Manual' : '/TrackDatTesting/Manual');
  const secondaryLabel = alternateManualLabel || (isTesting ? 'Main manual route' : 'Testing manual route');
  const backToTopId = documentRootId || 'trackdat-user-manual';
  const safeToc = useMemo(() => Array.isArray(toc) ? toc : [], [toc]);

  useEffect(() => {
    const updateProgress = () => {
      const root = document.documentElement;
      const available = Math.max(1, root.scrollHeight - window.innerHeight);
      setProgress(Math.min(100, Math.max(0, (window.scrollY / available) * 100)));
    };
    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, []);

  return (
    <>
      <Head>
        <title>{pageTitle} | ShuffleDB</title>
        <meta
          name="description"
          content={pageDescription}
        />
        <link rel="preload" href="/TrackDat/fonts/Oxanium-Regular.ttf" as="font" type="font/ttf" crossOrigin="anonymous" />
      </Head>

      <div className={styles.progressTrack} aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>

      <main className={styles.page}>
        <header className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>Official product guide</p>
            <h1>{pageTitle}</h1>
            <p className={styles.dek}>{pageDek}</p>
          </div>
          <nav className={styles.heroActions} aria-label="Manual actions">
            <Link className={styles.primaryAction} href={productRoute}>Open {resolvedProductName}</Link>
            {secondaryHref ? <Link className={styles.secondaryAction} href={secondaryHref}>{secondaryLabel}</Link> : null}
            <button className={styles.secondaryAction} type="button" onClick={() => window.print()}>Print / Save PDF</button>
          </nav>
        </header>

        <button
          className={styles.contentsToggle}
          type="button"
          aria-expanded={contentsOpen}
          onClick={() => setContentsOpen((value) => !value)}
        >
          {contentsOpen ? 'Hide contents' : 'Show contents'}
        </button>

        <div className={styles.layout}>
          <aside className={`${styles.contents} ${contentsOpen ? styles.contentsOpen : ''}`}>
            <div className={styles.contentsInner}>
              <p className={styles.contentsLabel}>On this page</p>
              <ol>
                {safeToc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} onClick={() => setContentsOpen(false)}>{item.label}</a>
                  </li>
                ))}
              </ol>
              <a className={styles.backToTop} href={`#${backToTopId}`}>Back to top ↑</a>
            </div>
          </aside>

          <article
            className={styles.article}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </main>
    </>
  );
}
